import { useTeam } from '../context/TeamContext';
import type { TeamMemberView } from '../types';

interface Props {
  member: TeamMemberView | null;
  size?: number;
  title?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// WCAG relative luminance → pick near-black text on pale chips, white on dark.
function readableTextColor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#fff';
  const n = parseInt(m[1], 16);
  const toLin = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = toLin((n >> 16) & 0xff);
  const g = toLin((n >> 8) & 0xff);
  const b = toLin(n & 0xff);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55 ? '#111827' : '#fff';
}

export function AssigneeAvatar({ member, size = 22, title }: Props) {
  const { outlineByUserId } = useTeam();
  const name = member?.displayName ?? '?';
  const bg = member?.color ?? '#64748b';
  const outline = member ? outlineByUserId[member.userId] : undefined;
  const ringWidth = Math.max(2, Math.round(size / 11));
  return (
    <span
      className="assignee-avatar"
      style={{
        width: size,
        height: size,
        backgroundColor: bg,
        color: readableTextColor(bg),
        fontSize: Math.max(10, size * 0.46),
        boxShadow: outline
          ? `0 0 0 ${ringWidth}px ${outline}, 0 1px 2px rgba(0,0,0,0.2)`
          : '0 1px 2px rgba(0,0,0,0.2)',
      }}
      title={title ?? name}
      aria-label={`Assigned to ${name}`}
    >
      {initials(name)}
    </span>
  );
}
